import { gql, type TypedDocumentNode } from "@apollo/client";
import type { PaginationVariables } from "@/components/Pagination/Pagination";
import type { CommissionLineFundingStatus, CommissionLineNode, CommissionLinePayoutEligibilityStatus } from "./types";

export interface CommissionLinesQueryResponse {
  commissionLines: {
    nodes: CommissionLineNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionLinesQueryVariables extends PaginationVariables {
  fundingStatus?: CommissionLineFundingStatus;
  payoutEligibilityStatus?: CommissionLinePayoutEligibilityStatus;
  campaignId?: string;
  crmPersonId?: string;
  fundingBatchId?: string;
  releaseBatchId?: string;
  paidOutFrom?: string;
  paidOutTo?: string;
  partnerId?: string;
}

export const COMMISSION_LINES_QUERY: TypedDocumentNode<CommissionLinesQueryResponse, CommissionLinesQueryVariables> =
  gql`
    query CommissionLines(
      $first: Int
      $after: String
      $last: Int
      $before: String
      $fundingStatus: BackofficeCommissionLineFundingStatus
      $payoutEligibilityStatus: BackofficeCommissionLinePayoutEligibilityStatus
      $campaignId: ID
      $crmPersonId: ID
      $fundingBatchId: ID
      $releaseBatchId: ID
      $paidOutFrom: ISO8601Date
      $paidOutTo: ISO8601Date
      $partnerId: ID
    ) {
      commissionLines(
        first: $first
        after: $after
        last: $last
        before: $before
        fundingStatus: $fundingStatus
        payoutEligibilityStatus: $payoutEligibilityStatus
        campaignId: $campaignId
        crmPersonId: $crmPersonId
        fundingBatchId: $fundingBatchId
        releaseBatchId: $releaseBatchId
        paidOutFrom: $paidOutFrom
        paidOutTo: $paidOutTo
        partnerId: $partnerId
      ) {
        nodes {
          id
          person {
            id
            displayName
            pgaId
          }
          amountCents
          fundingStatus
          payoutEligibilityStatus
          holdReasons
          lineKind
          receivedAt
          payableAt
          paidOutAt
          expiredAt
          facilityName
          fundingBatch {
            id
            status
          }
          releaseBatch {
            id
            status
          }
          splitLines {
            id
          }
          campaign {
            id
            editionYear
            partner {
              id
              name
            }
            program {
              id
              name
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `;

export interface CommissionLineQueryResponse {
  commissionLine: CommissionLineNode | null;
}

export interface CommissionLineQueryVariables {
  id: string;
}

export const COMMISSION_LINE_QUERY: TypedDocumentNode<CommissionLineQueryResponse, CommissionLineQueryVariables> = gql`
  query CommissionLine($id: ID!) {
    commissionLine(id: $id) {
      id
      person {
        id
        displayName
        pgaId
      }
      amountCents
      fundingStatus
      payoutEligibilityStatus
      holdReasons
      lineKind
      receivedAt
      payableAt
      paidOutAt
      expiredAt
      facilityName
      fundingBatch {
        id
        status
      }
      releaseBatch {
        id
        status
      }
      splitLines {
        id
      }
      campaign {
        id
        editionYear
        partner {
          id
          name
        }
        program {
          id
          name
        }
      }
    }
  }
`;
